import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { generateClient } from "aws-amplify/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  Trophy,
  ArrowLeft,
  Medal,
  CheckCircle,
  LayoutGrid,
} from "lucide-react";
import {
  getEvent,
  listEventPlayerResults,
  listPlayerConstructs,
} from "../graphql/queries";

const client = generateClient();

const EventDetail: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();

  // Fetch event data
  const {
    data: event,
    isLoading: eventLoading,
    error: eventError,
  } = useQuery({
    queryKey: ["event", eventId],
    queryFn: async () => {
      const result = await client.graphql({
        query: getEvent,
        variables: { id: eventId! },
      });
      return result.data.getEvent;
    },
    enabled: !!eventId,
  });

  // Fetch event player results
  const { data: eventPlayerResults, isLoading: resultsLoading } = useQuery({
    queryKey: ["eventPlayerResults", eventId],
    queryFn: async () => {
      let allItems: any[] = [];
      let nextToken: string | null | undefined = undefined;

      // Fetch all pages of results
      do {
        const result: any = await client.graphql({
          query: listEventPlayerResults,
          variables: {
            filter: { eventId: { eq: eventId } },
            limit: 100,
            nextToken: nextToken,
          },
        });

        allItems = [...allItems, ...result.data.listEventPlayerResults.items];
        nextToken = result.data.listEventPlayerResults.nextToken;
      } while (nextToken);

      return allItems;
    },
    enabled: !!eventId,
  });

  // Fetch player constructs
  const { data: playerConstructs, isLoading: constructsLoading } = useQuery({
    queryKey: ["playerConstructs"],
    queryFn: async () => {
      const result = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });
      return result.data.listPlayerConstructs.items;
    },
  });

  // Combine player results with player constructs
  const participants = React.useMemo(() => {
    if (!eventPlayerResults || !playerConstructs) return [];

    return eventPlayerResults
      .map((result: any) => {
        const construct = playerConstructs.find(
          (c: any) => c.id === result.playerConstructId
        );
        if (!construct) return null;

        return {
          id: result.id,
          name: construct.name,
          record: `${result.wins}-${result.losses}-${result.draws}`,
          points: result.points,
          rank: result.rank,
          omw: result.omw,
          gw: result.gw,
          ogw: result.ogw,
        };
      })
      .filter((p: any) => p !== null)
      .sort((a: any, b: any) => a.rank - b.rank);
  }, [eventPlayerResults, playerConstructs]);

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return "🥇";
      case 2:
        return "🥈";
      case 3:
        return "🥉";
      default:
        return null;
    }
  };

  const getTop8Status = (rank: number) => {
    if (rank <= 8) {
      return (
        <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
          <Medal className="h-3 w-3" />
          <span>Top 8</span>
        </div>
      );
    }
    return null;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Loading state
  if (eventLoading || resultsLoading || constructsLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Error state
  if (eventError || !event) {
    return (
      <div className="text-center text-red-600">Error loading event data</div>
    );
  }

  const getFormatIcon = (format: string) => {
    switch (format) {
      case "Standard":
        return "⚔️";
      case "Modern":
        return "🔥";
      case "Draft":
        return "📦";
      case "Commander":
        return "👑";
      case "Sealed":
        return "📋";
      default:
        return "🎮";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to={`/league/${event.leagueId}/events`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Events
              </Link>
            </Button>
          </div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <span className="text-3xl">{getFormatIcon(event.format)}</span>
            {event.name}
          </h1>
          {event.description && (
            <p className="text-muted-foreground">{event.description}</p>
          )}
        </div>
        <div className="flex gap-2 mt-4 sm:mt-0">
          {event.format === "Commander" && (
            <Button variant="outline" asChild>
              <Link to={`/event/${eventId}/commander-pods`}>
                <LayoutGrid className="mr-2 h-4 w-4" />
                View Pods
              </Link>
            </Button>
          )}
          {event.isCompleted && (
            <Button variant="outline" asChild>
              <Link to={`/admin/event/${eventId}/results`}>View Results</Link>
            </Button>
          )}
          {!event.isCompleted && (
            <Button asChild>
              <Link to={`/event/${eventId}/register`}>Register for Event</Link>
            </Button>
          )}
        </div>
      </div>

      {/* Event Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Date & Time</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">{formatDate(event.date)}</div>
            <p className="text-xs text-muted-foreground">
              {event.format} Tournament
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Location</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">{event.location}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Participants</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-sm font-medium">
              {participants.length} Players
            </div>
            <p className="text-xs text-muted-foreground">
              {event.isCompleted ? "Completed" : "Registered"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {event.isCompleted ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <Clock className="h-4 w-4 text-blue-600" />
              )}
              <span className="text-sm font-medium">
                {event.isCompleted ? "Completed" : "Upcoming"}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              {event.maxRounds} rounds
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Final Standings */}
        <Card>
          <CardHeader>
            <CardTitle>Final Standings</CardTitle>
            <CardDescription>Tournament results and rankings</CardDescription>
          </CardHeader>
          <CardContent>
            {participants.length > 0 ? (
              <div className="space-y-3">
                {participants.map((participant: any) => (
                  <div
                    key={participant.id}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">
                          {participant.rank}
                        </span>
                        {getRankIcon(participant.rank) && (
                          <span className="text-lg">
                            {getRankIcon(participant.rank)}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{participant.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {participant.record} • {participant.points} points
                        </p>
                        {getTop8Status(participant.rank)}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{participant.points} pts</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No standings available yet
              </p>
            )}
          </CardContent>
        </Card>

        {/* Event Information */}
        <Card>
          <CardHeader>
            <CardTitle>Event Information</CardTitle>
            <CardDescription>Tournament details and format</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Format Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Format:</span>
                    <span>{event.format}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Rounds:</span>
                    <span>{event.maxRounds}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Players:</span>
                    <span>{participants.length}</span>
                  </div>
                  {event.hasTopCut && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Top Cut:</span>
                      <span className="text-green-600">Yes</span>
                    </div>
                  )}
                  {event.isPremium && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Event Type:</span>
                      <span className="text-amber-600">Premium</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EventDetail;
